// app/src/lib/session.jsx — auth + the /v1 session context (D1-DESIGN §3). One provider owns:
// the auth glue (Firebase or the dev token), the api client, the /v1/me principal, and the
// credits balance (refreshed on sign-in, after terminal jobs, after top-up return, and on
// window refocus — the §5.1 contract).

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { initAuth } from '@common/auth.js';
import { makeApi } from '@common/api.js';

const SessionCtx = createContext(null);

const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN || null;
const FIREBASE_CONFIG = import.meta.env.VITE_FIREBASE_CONFIG ? JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG) : null;
const API_URL = import.meta.env.VITE_API_URL || ''; // '' in dev — the Vite proxy carries /v1

export function SessionProvider({ children }) {
  const auth = useMemo(() => initAuth(DEV_TOKEN ? { devToken: DEV_TOKEN } : { firebaseConfig: FIREBASE_CONFIG }), []);
  const [authed, setAuthed] = useState(null);   // null = resolving, false = signed out, true = in
  const [me, setMe] = useState(null);           // the /v1/me principal echo
  const [credits, setCredits] = useState(null); // { balance, plan, recent }
  // MAINT-MODE §8: two independent maintenance signals, kept separate so the ADMIN exemption is
  // server-authoritative (an admin's calls never 503, so they fall through to the live app even
  // while the global flag is on). `blocked` = the server refused THIS session (a 503 {maintenance},
  // i.e. a signed-in non-admin). `statusMaint` = the GLOBAL flag from the public /v1/status read
  // (drives the SIGNED-OUT dark-launch case, where there is no session to 503).
  const [blocked, setBlocked] = useState(null);       // { message } | null
  const [statusMaint, setStatusMaint] = useState(null); // { on, message } | null
  const api = useMemo(() => makeApi({
    baseUrl: API_URL, getToken: () => auth.getToken(),
    onMaintenance: (message) => setBlocked({ message: message || '' }),
  }), [auth]);

  // Resolve /v1/me. A transient blip (cold function / 5xx / network) must NOT null-out an
  // already-loaded `me` — that would strand the Verify-Email gate on its "Loading…" branch
  // with no poll (see the verify-gate review). So: a transient 'error' retries a couple times
  // and otherwise leaves `me` intact; a genuine 'auth' rejection means the client believes it
  // is signed in but the server rejects the token — clear the dead session so the UI returns
  // to sign-in instead of stranding forever on Loading.
  const refreshMe = useCallback(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const r = await api.get('me');
      if (r.kind === 'ok') { setMe(r.body); return r.body; }
      if (r.kind === 'auth') {
        setMe(null);
        try { await auth.signOut(); } catch { /* best effort — onChange(null) will land us on sign-in */ }
        return null;
      }
      if (attempt < 2) await new Promise((res) => setTimeout(res, 300 * (attempt + 1))); // transient — back off + retry
    }
    return null; // transient failures exhausted — keep the last-known `me`
  }, [api, auth]);

  const refreshCredits = useCallback(async () => {
    const r = await api.get('credits');
    if (r.kind === 'ok') setCredits(r.body);
    return r;
  }, [api]);

  useEffect(() => auth.onChange(async (u) => {
    if (!u) { setAuthed(false); setMe(null); setCredits(null); return; }
    setAuthed(true);
    await refreshMe();
    await refreshCredits();
  }), [auth, refreshMe, refreshCredits]);

  useEffect(() => {
    const onFocus = () => { if (authed) refreshCredits(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authed, refreshCredits]);

  // MAINT-MODE §8a: a public status read at boot so a dark launch replaces even the signed-out
  // sign-in screen. Fails OPEN (a status blip must never fake a lockout).
  useEffect(() => {
    let alive = true;
    api.get('status').then((r) => {
      if (!alive) return;
      setStatusMaint(r.kind === 'ok' ? { on: r.maintenance === true, message: r.message || '' } : { on: false, message: '' });
    });
    return () => { alive = false; };
  }, [api]);

  // the app is dark for THIS visitor when the server refused this session (signed-in non-admin),
  // or — signed OUT — when the global flag is on. An admin never lands here (their calls pass).
  const maintenance = useMemo(() => {
    if (blocked) return { on: true, message: blocked.message };
    if (authed === false && statusMaint && statusMaint.on) return { on: true, message: statusMaint.message };
    return { on: false, message: '' };
  }, [blocked, authed, statusMaint]);

  const value = { auth, api, authed, me, credits, maintenance, refreshMe, refreshCredits };
  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession() {
  return useContext(SessionCtx);
}
